import { useState } from "react";
import type { ColumnProps } from "@monolith-forensics/monolith-ui";
import { usePermissions } from "@/hooks/usePermissions";
import { useCaseFiles } from "@/Features/Files/Hooks/useCaseFiles";
import { CaseFilesTable } from "@/Features/Files/Tables/CaseFilesTable";
import { CaseFileFlyout } from "@/Features/Files/Flyouts/CaseFileFlyout";
import { DeleteCaseFileModal } from "@/Features/Files/Modals/DeleteCaseFileModal";
import { FILE_SOURCE_TYPES } from "@/Features/Files/files.types";
import type { File as CaseFile } from "@/Features/Files/files.types";

// Tells a validation report from a plain attachment, from the row alone.
const sourceColumn: ColumnProps = {
  dataField: "source_type",
  caption: "Source",
  render: ({ rowData }: { rowData: CaseFile }) =>
    rowData.source_type === FILE_SOURCE_TYPES.VALIDATION_REPORT
      ? "Validation report"
      : "Attachment",
};

/**
 * The Case Files table over one asset's attachments and validation reports,
 * modelled on the evidence item Files table minus everything case-scoped: no
 * edit (attachments are immutable), no Relay share, no case logs.
 */
export const AssetAttachmentsTable: React.FC<{ assetUuid: string }> = ({
  assetUuid,
}) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { handleFileClick, uploadFiles, tableRef } = useCaseFiles();

  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CaseFile | null>(null);

  const canUpload = hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_CREATE);
  const canDelete = hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_DELETE);

  const closeDeleteFileModal = () => {
    setShowDeleteFileModal(false);
    setSelectedItem(null);
  };

  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      uploadFiles(Array.from(files));

      input.remove();
    };
    input.click();
  };

  const handleDownload = (rowData: CaseFile) => {
    const downloadUrl = rowData?.link?.replace(
      "localhost:3000",
      "localhost:3001"
    ); // replace is used for local development

    if (downloadUrl) {
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = rowData.name;
      anchor.click();
      anchor.remove();
    }
  };

  const select = (setShow: (show: boolean) => void) => (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShow(true);
  };

  return (
    <>
      <CaseFilesTable
        tableRef={tableRef}
        stateStoreKey="asset:attachments:table"
        // include_pending: an upload in progress shows as an uploading row,
        // as on Case Files.
        query={{ asset_uuid: assetUuid, include_pending: true }}
        extraColumns={[sourceColumn]}
        onUploadFile={canUpload ? handleUploadFile : undefined}
        dropDisabled={!canUpload}
        onActionButtonClick={select(setShowInfoPanel)}
        onShowDetails={select(setShowInfoPanel)}
        onDownload={handleDownload}
        onView={handleFileClick}
        onDelete={canDelete ? select(setShowDeleteFileModal) : undefined}
      />
      {showInfoPanel && selectedItem && (
        <CaseFileFlyout
          open={showInfoPanel}
          onClose={() => setShowInfoPanel(false)}
          onDelete={
            canDelete
              ? () => {
                  setShowInfoPanel(false);
                  setShowDeleteFileModal(false);
                }
              : undefined
          }
          defaultData={selectedItem}
        />
      )}
      {showDeleteFileModal && selectedItem && (
        <DeleteCaseFileModal
          show={showDeleteFileModal}
          onClose={closeDeleteFileModal}
          onCancel={closeDeleteFileModal}
          onSubmit={closeDeleteFileModal}
          defaultFormData={selectedItem}
        />
      )}
    </>
  );
};

export default AssetAttachmentsTable;
